/**
 * Drag/paste attachment helpers — mirror the Obsidian plugin so the
 * web chat accepts the same files with the same constraints.
 *
 * KEEP IN SYNC with:
 *   - app/workflows/concierge/schema.py (ALLOWED_IMAGE_TYPES /
 *     ALLOWED_DOCUMENT_TYPES / MAX_ATTACHMENT_BYTES /
 *     MAX_ATTACHMENTS_PER_TURN)
 *   - ~/_notes/.obsidian/plugins/concierge/main.js (ATTACHMENT_IMAGE_MIMES /
 *     ATTACHMENT_DOC_MIMES / isAttachableFile / readFileAsBase64)
 *
 * If either side moves without the other, valid drops will get rejected
 * at the API boundary or silently lost in the UI.
 */

/** Anthropic vision accepts JPEG/PNG/GIF/WebP (no HEIC). */
export const ATTACHMENT_IMAGE_MIMES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
])

/** PDF goes through Anthropic's document block directly. `.docx` is
 *  accepted on the wire but the backend text-extracts via python-docx
 *  before handing to the model. */
export const ATTACHMENT_DOC_MIMES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])

/** 5 MB per file — matches Anthropic's per-image upload cap. */
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024
/** 10 per turn — matches the backend schema's MAX_ATTACHMENTS_PER_TURN. */
export const ATTACHMENT_MAX_COUNT = 10

export interface PendingAttachment {
  kind: 'image' | 'document'
  mime: string
  filename: string
  size: number
  data_base64: string
}

/** True if this dropped File should be accepted as an attachment. */
export function isAttachableFile(file: File): boolean {
  if (!file) return false
  if (file.size > ATTACHMENT_MAX_BYTES) return false
  const t = (file.type || '').toLowerCase()
  if (ATTACHMENT_IMAGE_MIMES.has(t) || ATTACHMENT_DOC_MIMES.has(t)) return true
  // Some drag sources (Finder on macOS, Word itself) report an empty
  // `type`. Fall back to extension sniffing — same gates as the plugin.
  const name = (file.name || '').toLowerCase()
  if (name.endsWith('.pdf') || name.endsWith('.docx')) return true
  return false
}

/** Resolve a missing `file.type` from the extension so the wire payload
 *  carries the right MIME on Finder-sourced drops. */
export function inferMime(file: File): string {
  const t = (file.type || '').toLowerCase()
  if (t) return t
  const name = (file.name || '').toLowerCase()
  if (name.endsWith('.pdf')) return 'application/pdf'
  if (name.endsWith('.docx'))
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  if (name.endsWith('.png')) return 'image/png'
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg'
  if (name.endsWith('.gif')) return 'image/gif'
  if (name.endsWith('.webp')) return 'image/webp'
  return ''
}

export function kindForMime(mime: string): 'image' | 'document' {
  return ATTACHMENT_DOC_MIMES.has(mime) ? 'document' : 'image'
}

/** Read a File into base64. Strips the `data:<mime>;base64,` prefix so
 *  the wire payload is just the encoded bytes — matches the plugin. */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const r = String(reader.result || '')
      const i = r.indexOf('base64,')
      resolve(i >= 0 ? r.slice(i + 'base64,'.length) : '')
    }
    reader.onerror = () => reject(reader.error || new Error('read failed'))
    reader.readAsDataURL(file)
  })
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}K`
  return `${(n / (1024 * 1024)).toFixed(1)}M`
}

/* ─── .eml file → message:// link ──────────────────────────────────────
 * Dropping a saved email file (from Finder / iOS Files / mail clients
 * that export .eml) is treated as "I want a clickable bounce-back to
 * the original message in Mail.app" — NOT an attachment upload. Only
 * `Subject` and `Message-ID` are needed; everything else (body, MIME
 * parts, attachments inside the .eml) is ignored. The resulting
 * `message://%3C<id>%3E` URL only resolves on a Mac where Apple Mail
 * has the same message indexed locally.
 * ──────────────────────────────────────────────────────────────────── */

export function isEmlFile(file: File): boolean {
  if (!file) return false
  const t = (file.type || '').toLowerCase()
  if (t === 'message/rfc822') return true
  return (file.name || '').toLowerCase().endsWith('.eml')
}

/** Read the leading slice of a file as text. 64KB is far more than
 *  enough for the header block — RFC 5322 sets no hard ceiling but
 *  in practice mail headers are under 8KB; capping the read avoids
 *  pulling a multi-MB body+attachments blob we never inspect. */
function readFileHeaderAsText(
  file: File,
  maxBytes = 64 * 1024
): Promise<string> {
  const slice = file.size > maxBytes ? file.slice(0, maxBytes) : file
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(reader.error || new Error('read failed'))
    reader.readAsText(slice)
  })
}

/** Decode RFC 2047 encoded-words (`=?charset?B?...?=` / `=?charset?Q?...?=`)
 *  so subjects with emoji / accents render right in the markdown link.
 *  Whitespace between adjacent encoded-words is dropped per §6.2. */
function decodeRfc2047(input: string): string {
  if (!input.includes('=?')) return input
  try {
    return input.replace(
      /=\?([^?]+)\?([BbQq])\?([^?]*)\?=(?:\s+(?==\?))?/g,
      (_full, charset: string, enc: string, data: string) => {
        let bytes: Uint8Array
        if (enc.toUpperCase() === 'B') {
          const bin = atob(data)
          bytes = new Uint8Array(bin.length)
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        } else {
          const qp = data.replace(/_/g, ' ')
          const out: number[] = []
          for (let i = 0; i < qp.length; i++) {
            if (qp[i] === '=' && i + 2 < qp.length) {
              const hex = qp.slice(i + 1, i + 3)
              if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
                out.push(parseInt(hex, 16))
                i += 2
                continue
              }
            }
            out.push(qp.charCodeAt(i))
          }
          bytes = new Uint8Array(out)
        }
        try {
          return new TextDecoder(charset).decode(bytes)
        } catch {
          return new TextDecoder('utf-8').decode(bytes)
        }
      }
    )
  } catch {
    return input
  }
}

/** Parse a `.eml` file and return a `message://` link with the
 *  Subject as label. Returns null when no Message-ID header is
 *  present (without it the link can't bounce back to a specific
 *  message). RFC 5322 header unfolding is applied; the body is
 *  not read. */
export async function extractEmlLink(
  file: File
): Promise<{ url: string; label: string } | null> {
  const raw = await readFileHeaderAsText(file)
  // Headers end at the first blank line (CRLF or LF).
  const headerEnd = raw.search(/\r?\n\r?\n/)
  const headerBlock = headerEnd >= 0 ? raw.slice(0, headerEnd) : raw
  // Unfold continuation lines (start with WSP, per §2.2.3).
  const unfolded = headerBlock.replace(/\r?\n[ \t]+/g, ' ')
  let messageId = ''
  let subject = ''
  for (const line of unfolded.split(/\r?\n/)) {
    const colon = line.indexOf(':')
    if (colon < 0) continue
    const name = line.slice(0, colon).trim().toLowerCase()
    const value = line.slice(colon + 1).trim()
    if (name === 'message-id' && !messageId) {
      const m = value.match(/<([^>]+)>/)
      messageId = m ? m[1] : value.replace(/^<|>$/g, '').trim()
    } else if (name === 'subject' && !subject) {
      subject = decodeRfc2047(value)
    }
    if (messageId && subject) break
  }
  if (!messageId) return null
  return {
    url: `message://%3C${messageId}%3E`,
    label: subject || file.name || '',
  }
}

/* ─── Email body extraction from Mail.app drags ────────────────────────
 * When dragging from Mail.app, the dataTransfer carries the message://
 * URL AND (sometimes) the rendered HTML body. Pull the body into a
 * paste-friendly text block so the agent can read the email directly
 * without needing to web_fetch a `message://` URL the API can't reach.
 * ──────────────────────────────────────────────────────────────────── */

const EMAIL_BODY_MAX_CHARS = 50_000
const EMAIL_BODY_MIN_CHARS = 50

export function extractEmailBodyFromDrag(dt: DataTransfer | null): string | null {
  if (!dt || !dt.getData) return null
  const html = dt.getData('text/html')
  if (!html || html.length < 20) return null
  let text: string
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    doc.querySelectorAll('script, style, head').forEach((el) => el.remove())
    doc.querySelectorAll('br').forEach((br) =>
      br.replaceWith(doc.createTextNode('\n'))
    )
    doc
      .querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, tr, blockquote, pre')
      .forEach((el) => el.appendChild(doc.createTextNode('\n')))
    text = (doc.body ? doc.body.textContent || '' : '')
      .replace(/\n[\t ]+/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  } catch {
    return null
  }
  if (!text || text.length < EMAIL_BODY_MIN_CHARS) return null
  if (text.length > EMAIL_BODY_MAX_CHARS) {
    text = text.slice(0, EMAIL_BODY_MAX_CHARS) + '\n[truncated]'
  }
  return text
}

// MIME types that browsers + macOS apps use for "this drag is a URL".
// Different browser + source-app combinations populate different keys.
// We sweep ALL of them and pick the first that matches our URL regex.
const URL_BEARING_DT_TYPES = [
  'text/uri-list',
  'text/x-moz-url',
  'text/x-moz-url-data',
  'application/x-moz-file-promise-url',
  'text/x-uri',
  'URL',
  'text/uri',
]

const URL_PROTOCOL_RE = /\b(message|mailto|https?|tel|ftp):\/\/\S+/

/** Pull the best link out of a drag's dataTransfer. Returns the URL +
 *  an optional label (Mail.app drops "Subject\nFrom..." as text/plain).
 *  Returns null when no recognizable URL is present.
 *
 *  Sweeps every URL-bearing type browsers expose because what's
 *  populated depends on browser × source-app: Mail.app drags into
 *  Safari can land in text/uri-list, text/plain, OR a Mac-specific
 *  type; Chrome strips message:// from text/uri-list in some macOS
 *  versions and leaves it in text/plain; etc. We try them all and
 *  match the first protocol-bearing string.
 */
export function extractUrlFromDrag(
  dt: DataTransfer | null
): { url: string; label: string } | null {
  if (!dt || !dt.getData) return null

  // Pass 1 — types macOS/browsers traditionally use for URL payloads.
  let url = ''
  for (const t of URL_BEARING_DT_TYPES) {
    const raw = safeGet(dt, t)
    if (!raw) continue
    // text/uri-list can carry many lines (URLs) separated by CRLF; #
    // lines are comments per the RFC. First non-comment URL wins.
    const candidate =
      raw.split(/\r?\n/).find((l) => l && !l.startsWith('#')) || ''
    if (candidate && URL_PROTOCOL_RE.test(candidate)) {
      url = candidate.match(URL_PROTOCOL_RE)![0]
      break
    }
  }

  // Pass 2 — text/plain anywhere in the payload. Mail.app often dumps
  // "Subject\nFrom: ...\nmessage://..." here.
  let plain = ''
  if (!url) {
    plain = safeGet(dt, 'text/plain') || ''
    const m = plain.match(URL_PROTOCOL_RE)
    if (m) url = m[0]
  } else {
    plain = safeGet(dt, 'text/plain') || ''
  }

  // Pass 3 — desperate sweep across every type the browser exposes.
  // Catches anything in a custom MIME (`com.apple.mail.email`, etc.)
  // that happens to embed a recognizable URL.
  if (!url && dt.types) {
    for (const t of Array.from(dt.types)) {
      if (URL_BEARING_DT_TYPES.includes(t) || t === 'text/plain') continue
      const raw = safeGet(dt, t)
      if (!raw) continue
      const m = raw.match(URL_PROTOCOL_RE)
      if (m) {
        url = m[0]
        break
      }
    }
  }

  if (!url) return null

  // Strip trailing whitespace/quotes that some browsers tack on.
  url = url.replace(/[)\]"'>]+$/, '')
  const label = (plain.split(/\r?\n/)[0] || '').trim()
  return { url, label }
}

/** dataTransfer.getData() throws on Safari when called for a type
 *  that's not in the current event's protected payload list. Wrap so
 *  one bad type doesn't break the whole sweep. */
function safeGet(dt: DataTransfer, type: string): string {
  try {
    return dt.getData(type) || ''
  } catch {
    return ''
  }
}

/** Build a `{type → value}` snapshot of everything the browser
 *  surfaces on a drop, for console diagnostics. Returns an empty
 *  object when `dt.types` is unavailable. */
export function snapshotDragTransfer(
  dt: DataTransfer | null
): Record<string, string> {
  if (!dt || !dt.types) return {}
  const out: Record<string, string> = {}
  for (const t of Array.from(dt.types)) {
    out[t] = safeGet(dt, t)
  }
  // Files come through .files, not .getData().
  if (dt.files && dt.files.length) {
    out['__files'] = Array.from(dt.files)
      .map((f) => `${f.name || '?'} (${f.type || '?'}, ${f.size}B)`)
      .join(' | ')
  }
  return out
}
